import React, { useEffect, useMemo, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS } from '../../config/environment';
import { fleetToast } from '../../utils/toast';
import { buildManualShiftTimes, toLocalInputValue } from './liveMapRules';

/**
 * Haritadan kurye ata / başka kuryeye ver. Üç giriş, TEK pencere: paketi kuryenin üstüne
 * sürükle-bırak (hedef önseçili gelir), pakete sağ tık, karttaki "Kurye değiştir / Kurye ata".
 * Onaysız atama yok: yanlış bırakma geri alınamazdı. Kayıt Teslimatlar ekranıyla aynı uçtan geçer
 * (kapasite, bölge kapsamı, bakiye kuralları sunucuda).
 */
export function AssignCourierModal({ target, fallbackCouriers, onClose, onDone }) {
    const [suggestions, setSuggestions] = useState(null);   // null = yükleniyor
    const [selectedId, setSelectedId] = useState('');
    const [saving, setSaving] = useState(false);

    const currentId = target.currentCourier?.id || null;

    useEffect(() => {
        let cancelled = false;
        (async () => {
            let list = [];
            try {
                const res = await apiService.get(API_ENDPOINTS.FLEET_PANEL.DELIVERY_ASSIGNMENT_SUGGESTIONS(target.deliveryId));
                if (res?.success && Array.isArray(res.data)) list = res.data;
            } catch { /* öneri gelmezse düz liste gösterilir */ }
            if (cancelled) return;
            setSuggestions(list);
            const selectable = (id) => list.some((s) => s.courierId === id && s.hasLocation && !s.atCapacity);
            if (target.initialCourierId && (list.length === 0 || selectable(target.initialCourierId))) {
                setSelectedId(target.initialCourierId);
            } else if (!currentId) {
                const suggested = list.find((s) => s.isSuggested && s.hasLocation && !s.atCapacity);
                if (suggested) setSelectedId(suggested.courierId);
            }
        })();
        return () => { cancelled = true; };
    }, [target.deliveryId, target.initialCourierId, currentId]);

    // Öneri gelmediyse mesaideki ve mesaide olmayan kuryeler düz listelenir (mevcut kurye hariç).
    const plain = useMemo(
        () => (fallbackCouriers || []).filter((c) => c.courierId !== currentId),
        [fallbackCouriers, currentId],
    );

    const droppedOn = target.initialCourierId
        ? (suggestions || []).find((s) => s.courierId === target.initialCourierId)
        : null;
    const droppedBlocked = droppedOn && (!droppedOn.hasLocation || droppedOn.atCapacity);

    const save = async () => {
        if (!selectedId || saving) return;
        setSaving(true);
        try {
            const res = await apiService.put(API_ENDPOINTS.FLEET_PANEL.DELIVERY_ASSIGN_COURIER, {
                deliveryId: target.deliveryId,
                courierId: selectedId,
            });
            if (res?.success) {
                fleetToast.success(currentId ? 'Paket seçtiğiniz kuryeye verildi' : 'Kurye atandı');
                onDone();
            } else {
                fleetToast.error(res?.error || res?.message || 'Kurye atanamadı, tekrar deneyin');
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={() => !saving && onClose()}>
            <div className="modal lm-modal-md" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{currentId ? 'Kurye değiştir' : 'Kurye ata'}</h2>
                    <button className="modal-close" onClick={onClose} disabled={saving} aria-label="Kapat"><X size={20} /></button>
                </div>
                <div className="modal-body">
                    <p className="lm-assign-what"><strong>{target.subtitle}</strong></p>
                    {target.currentCourier && (
                        <p className="lm-assign-note">Şu an {target.currentCourier.name} üzerinde.</p>
                    )}
                    {droppedBlocked && (
                        <p className="lm-assign-warn">
                            {droppedOn.courierName} {droppedOn.atCapacity ? 'taşıyabileceği paket sayısına ulaştı' : 'konum göndermiyor'}; başka bir kurye seçin.
                        </p>
                    )}

                    {suggestions === null ? (
                        <div className="lm-assign-loading"><Loader2 size={16} className="lm-spin" /> Kuryeler getiriliyor…</div>
                    ) : suggestions.length > 0 ? (
                        <div className="lm-assign-list" role="radiogroup" aria-label="Kurye seçin">
                            {suggestions.filter((s) => s.courierId !== currentId).map((s) => {
                                const selectable = s.hasLocation && !s.atCapacity;
                                const selected = selectedId === s.courierId;
                                const idle = s.minutesIdle >= 9999 ? 'hiç paket almadı' : `${s.minutesIdle} dk boşta`;
                                return (
                                    <button
                                        type="button"
                                        key={s.courierId}
                                        role="radio"
                                        aria-checked={selected}
                                        className={`lm-assign-item${selected ? ' selected' : ''}`}
                                        disabled={!selectable}
                                        onClick={() => setSelectedId(s.courierId)}
                                    >
                                        <span className={`lm-assign-radio${selected ? ' on' : ''}`} />
                                        <span className="lm-assign-main">
                                            <span className="lm-assign-name">
                                                {s.courierName || 'İsimsiz kurye'}
                                                {s.isSuggested && <span className="lm-assign-tag">Sıradaki</span>}
                                                {!s.hasLocation && <span className="lm-assign-tag warn">Konum yok</span>}
                                                {s.atCapacity && <span className="lm-assign-tag warn">Dolu</span>}
                                            </span>
                                            <span className="lm-assign-meta">
                                                {[
                                                    s.distanceKm != null ? `${s.distanceKm.toFixed(1)} km uzakta` : null,
                                                    `üzerinde ${s.activeDeliveries} paket`,
                                                    idle,
                                                ].filter(Boolean).join(', ')}
                                            </span>
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    ) : plain.length > 0 ? (
                        <div className="lm-assign-list" role="radiogroup" aria-label="Kurye seçin">
                            {plain.map((c) => {
                                const selected = selectedId === c.courierId;
                                return (
                                    <button
                                        type="button"
                                        key={c.courierId}
                                        role="radio"
                                        aria-checked={selected}
                                        className={`lm-assign-item${selected ? ' selected' : ''}`}
                                        onClick={() => setSelectedId(c.courierId)}
                                    >
                                        <span className={`lm-assign-radio${selected ? ' on' : ''}`} />
                                        <span className="lm-assign-main">
                                            <span className="lm-assign-name">{c.courierName || 'İsimsiz kurye'}</span>
                                            {c.offShift && <span className="lm-assign-meta">Mesaide değil</span>}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    ) : (
                        <p className="lm-assign-note">Atanabilecek kurye bulunamadı.</p>
                    )}
                </div>
                <div className="modal-actions">
                    <button className="btn btn-secondary" onClick={onClose} disabled={saving}>Vazgeç</button>
                    <button className="btn btn-primary" onClick={save} disabled={!selectedId || saving}>
                        {saving ? 'Atanıyor…' : (currentId ? 'Bu kuryeye ver' : 'Kurye ata')}
                    </button>
                </div>
            </div>
        </div>
    );
}

/**
 * Mesaiyi bitir. Elle girilen saatler YALNIZ kurye mesai raporuna yansır; sistemin vardiya kaydı
 * (hakediş, paket ataması) olduğu gibi kalır. Kuryede paket varsa sunucu adet söyleyerek reddeder.
 */
export function EndShiftModal({ courier, busy, onClose, onConfirm }) {
    const [manualOpen, setManualOpen] = useState(false);
    const [start, setStart] = useState('');
    const [end, setEnd] = useState(() => toLocalInputValue(new Date()));

    const manual = manualOpen ? buildManualShiftTimes(start, end, new Date()) : { error: null, body: null };
    const packageCount = (courier.orders || []).length;

    return (
        <div className="modal-overlay" onClick={() => !busy && onClose()}>
            <div className="modal lm-modal-sm" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>Mesaiyi bitir</h2>
                    <button className="modal-close" onClick={onClose} disabled={busy} aria-label="Kapat"><X size={20} /></button>
                </div>
                <div className="modal-body lm-endshift">
                    <p>
                        <strong>{courier.courierName}</strong> mesaiden çıkarılacak: haritadan kalkar ve yeni paket almaz.
                        Vardiya bitişi kaydedilir.
                    </p>
                    {packageCount > 0 ? (
                        <p className="lm-endshift-warn">
                            Kuryenin üzerinde <strong>{packageCount} paket</strong> var. Paketler teslim edilmeden ya da başka
                            kuryeye verilmeden mesai bitirilemez.
                        </p>
                    ) : (
                        <p className="lm-endshift-note">Kurye başka bir firmanın paketini taşıyorsa işlem yine reddedilir.</p>
                    )}

                    <label className="lm-check">
                        <input type="checkbox" checked={manualOpen} onChange={(e) => setManualOpen(e.target.checked)} />
                        Mesai saatlerini elle gir
                    </label>
                    {manualOpen && (
                        <div className="lm-manual">
                            <div className="lm-manual-grid">
                                <label>
                                    Başlangıç
                                    <input type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} aria-invalid={!!manual.error} />
                                </label>
                                <label>
                                    Bitiş
                                    <input type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} aria-invalid={!!manual.error} />
                                </label>
                            </div>
                            <p className={manual.error ? 'lm-manual-error' : 'lm-endshift-note'}>
                                {manual.error
                                    || 'Yalnız kurye mesai raporunda görünür; hakediş ve sistem kaydı değişmez. Boş bırakılan alan için sistem saati kullanılır.'}
                            </p>
                        </div>
                    )}
                </div>
                <div className="modal-actions">
                    <button className="btn btn-secondary" onClick={onClose} disabled={busy}>Vazgeç</button>
                    <button className="btn btn-danger" onClick={() => onConfirm(manual.body)} disabled={busy || !!manual.error}>
                        {busy ? 'Bitiriliyor…' : 'Mesaiyi bitir'}
                    </button>
                </div>
            </div>
        </div>
    );
}
